import { cn } from '@/shared/lib'
import { designTokens } from '@/shared/lib/design-tokens'

export type PaymentHistoryStatus = 'success' | 'declined'
export type PaymentHistoryVariant = 'desktop' | 'mobile'

export type PaymentHistoryRowProps = {
  status: PaymentHistoryStatus
  date: string
  planName: string
  amount: string
  variant?: PaymentHistoryVariant
  tone?: 'default' | 'catalog-dark'
  className?: string
}

const typographyStyle = (token: { size: string; lineHeight: string; weight: number }) => ({
  fontFamily: designTokens.typography.fontFamilies.primary,
  fontSize: token.size,
  lineHeight: token.lineHeight,
  fontWeight: token.weight,
})

export function PaymentHistoryRow({
  status,
  date,
  planName,
  amount,
  variant = 'mobile',
  tone = 'default',
  className,
}: PaymentHistoryRowProps) {
  const isSuccess = status === 'success'
  const iconColor = isSuccess
    ? designTokens.colors.status.subscriptionSuccessIcon
    : designTokens.colors.status.subscriptionFailedIcon
  const statusLabel = isSuccess ? 'покупка' : 'платеж не прошел'

  const badgeTokens = isSuccess
    ? {
        background: designTokens.colors.status.adminSuccessBg,
        borderColor: designTokens.colors.status.adminSuccessBorder,
        textColor: designTokens.colors.status.adminSuccessText,
      }
    : {
        background: designTokens.colors.status.subscriptionDeclinedBg,
        borderColor: designTokens.colors.status.subscriptionDeclinedBorder,
        textColor: designTokens.colors.status.subscriptionDeclinedText,
      }

  const dateStyle =
    variant === 'desktop'
      ? {
          ...typographyStyle(designTokens.typography.subscription.historyText),
          color: tone === 'catalog-dark' ? '#FEFEFE' : designTokens.colors.text.primary,
        }
      : {
          ...typographyStyle(designTokens.typography.body.base),
          color:
            tone === 'catalog-dark'
              ? designTokens.colors.text.inverse
              : designTokens.colors.text.primary,
        }
  const planStyle =
    variant === 'desktop'
      ? {
          ...typographyStyle(designTokens.typography.subscription.historyText),
          color: tone === 'catalog-dark' ? '#FEFEFE' : designTokens.colors.text.primary,
        }
      : {
          ...typographyStyle(designTokens.typography.body.md),
          color:
            tone === 'catalog-dark'
              ? designTokens.colors.text.inverse
              : designTokens.colors.text.subscriptionHistoryPrimary,
        }
  const amountStyle =
    variant === 'desktop'
      ? {
          ...typographyStyle(designTokens.typography.subscription.historyText),
          color: tone === 'catalog-dark' ? '#FEFEFE' : designTokens.colors.text.strong,
          textAlign: 'right' as const,
        }
      : {
          ...typographyStyle(designTokens.typography.body.base),
          color:
            tone === 'catalog-dark'
              ? designTokens.colors.text.inverse
              : designTokens.colors.text.subscriptionHistoryPrimary,
          textAlign: 'right' as const,
        }
  const statusNoteStyle = {
    ...typographyStyle(designTokens.typography.subscription.historyNote),
    color:
      tone === 'catalog-dark'
        ? designTokens.colors.text.inverse
        : designTokens.colors.text.subscriptionHistoryNote,
  }

  const icon = (
    <span
      className="flex shrink-0 items-center justify-center"
      role="img"
      aria-label={statusLabel}
      style={{
        width: designTokens.sizes.width.subscriptionHistoryStatusIcon,
        height: designTokens.sizes.height.subscriptionHistoryStatusIcon,
        color: iconColor,
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width={designTokens.sizes.width.subscriptionHistoryStatusIcon}
        height={designTokens.sizes.height.subscriptionHistoryStatusIcon}
        aria-hidden="true"
      >
        {isSuccess ? (
          <path
            d="M6 12.5l3.5 3.5L18 8.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={designTokens.borders.widths.checkbox}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M8 8l8 8M16 8l-8 8"
            fill="none"
            stroke="currentColor"
            strokeWidth={designTokens.borders.widths.checkbox}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </span>
  )

  const planNode = (
    <span className="min-w-0 flex-1 truncate" style={planStyle}>
      {planName}
    </span>
  )
  const amountNode = (
    <span className="whitespace-nowrap text-right shrink-0" style={amountStyle}>
      {amount}
    </span>
  )

  if (variant === 'desktop') {
    return (
      <div
        className={cn('grid w-full min-w-0 items-center', className)}
        style={{
          columnGap: designTokens.spacing.md,
          gridTemplateColumns: `minmax(0, ${designTokens.sizes.width.subscriptionPaymentHistoryDate}) minmax(0, ${designTokens.sizes.width.subscriptionPaymentHistoryPlan}) minmax(0, ${designTokens.sizes.width.subscriptionPaymentHistoryStatus}) minmax(0, ${designTokens.sizes.width.subscriptionPaymentHistoryAmount})`,
        }}
      >
        <span className="whitespace-nowrap" style={dateStyle}>
          {date}
        </span>
        <span className="truncate" style={planStyle}>
          {planName}
        </span>
        <span
          className="inline-flex items-center justify-center whitespace-nowrap"
          style={{
            height: designTokens.sizes.height.subscriptionPaymentHistoryBadge,
            padding: `${designTokens.spacing.subscriptionPaymentHistoryBadgeY} ${designTokens.spacing.subscriptionPaymentHistoryBadgeX}`,
            borderRadius: designTokens.borders.radius.pill,
            borderWidth: designTokens.borders.widths.thin,
            borderStyle: designTokens.borders.styles.solid,
            background: badgeTokens.background,
            borderColor: badgeTokens.borderColor,
          }}
        >
          <span
            style={{
              ...typographyStyle(designTokens.typography.subscription.historyBadge),
              color: badgeTokens.textColor,
            }}
          >
            {statusLabel}
          </span>
        </span>
        {amountNode}
      </div>
    )
  }

  return (
    <div className={cn('w-full', className)}>
      <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.xxs }}>
        <div className="flex w-full items-center" style={{ gap: designTokens.spacing.lg }}>
          {icon}
          <span className="flex-1 whitespace-nowrap" style={dateStyle}>
            {date}
          </span>
          {!isSuccess ? (
            <span className="shrink-0 text-right" style={statusNoteStyle}>
              Списание не прошло
            </span>
          ) : null}
        </div>
        <div className="flex w-full items-start justify-between">
          {planNode}
          {amountNode}
        </div>
      </div>
    </div>
  )
}
